import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButtons from "./components/ShareButtons";
import RelatedPanel from "./components/RelatedPanel";
import { useSEO, truncate } from "@/hooks/useSEO";
import { getItemById, getRelated, formatDate, typeLabels, typePaths } from "@/utils/content";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = id ? getItemById(id) : undefined;

  useSEO({
    title: item ? `${item.title} - 白峰美羽` : "内容未找到 - 白峰美羽",
    description: item ? truncate(item.summary, 120) : "抱歉，未能找到对应的内容。",
    keywords: item ? `白峰美羽,${item.tags.join(",")}` : "白峰美羽",
  });

  if (!item) {
    return (
      <section className="max-w-site mx-auto px-4 md:px-6 py-24 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-6">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-heading font-semibold text-foreground-900">
          抱歉，未能找到这条内容
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          可能是链接有误或内容已被移除，您可以返回重新浏览。
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(0)}
            className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            刷新页面
          </button>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  const related = getRelated(item, 4);

  return (
    <>
      <section className="bg-primary-900">
        <div className="max-w-site mx-auto px-4 md:px-6 pt-8 md:pt-10 pb-8">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: typeLabels[item.type], path: typePaths[item.type] },
              { label: item.title },
            ]}
          />
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-accent-500 text-accent-950 text-xs font-medium">
              {item.category}
            </span>
            <span className="text-primary-200 text-xs">{typeLabels[item.type]}</span>
          </div>
          <h1 className="mt-4 text-2xl md:text-4xl font-heading font-semibold text-background-50 leading-tight max-w-3xl">
            {item.title}
          </h1>
          <div className="mt-5 flex items-center gap-5 text-xs md:text-sm text-primary-200 flex-wrap">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(item.date)}
            </span>
          </div>
        </div>
      </section>

      <section className="max-w-site mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12">
          <article className="flex-1 min-w-0">
            <div className="w-full h-64 md:h-[420px] rounded-lg overflow-hidden mb-8">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 白峰美羽`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-content space-y-5">
              {item.body.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base leading-7 md:leading-8 text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200"
                >
                  # {tag}
                </span>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between flex-wrap gap-4">
              <ShareButtons />
              <Link
                to={typePaths[item.type]}
                className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回{typeLabels[item.type]}列表
              </Link>
            </div>
          </article>

          <RelatedPanel related={related} />
        </div>
      </section>
    </>
  );
}